import React, { useEffect, useState, createContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { Slider } from '@mui/material';
import dayjs from 'dayjs';
// eslint-disable-next-line import/no-cycle
import { TimesheetsHeader, MainTable, TotalSection, Attachments } from './index';
import { useBlocker, usePrevious, useWindowSize } from '../../../utils/hooks';
import { appNotificationsTypes, months, responsiveSizeTimesheet, timesheetsStatuses } from '../../../utils/constants';
import NavigationComponent from '../../common/NavigationComponent/NavigationComponent';
import { tneAxiosClient } from '../../../api/apiSettings';
import BaseModal from '../../common/ModalWindows/BaseModal';
import WithCloseIcon from '../../common/ComposableWrappers/WithCloseIcon/WithCloseIcon';
import {
  addNewTimesheet,
  changeStatus,
  deleteLineById,
  restoreDeletedTimesheetLine,
  saveComment,
  updateComment
} from '../../../actions/timesheet';
import { getListOfApprovers } from '../../../actions/user';
import NotificationModal from '../../common/ModalWindows/NotificationModal';
import SubmitModal from './SubmitModal/SubmitModal';
import ApproverComment from './ApproverComment/ApproverComment';
import {
  SavedPopUp,
  UnsavedChanges,
  DeletePopUp,
  FinalModal,
  ConfirmationModal,
  RejectModal,
  CopyModal
} from './components/modals/modals';
import { basicErrorHandler, getSerializedSearchParams } from '../../../utils/helpers';
import classes from './Timesheet.module.scss';
import MobileHeadMenu from './components/MobileHeadMenu/MobileHeadMenu';
// eslint-disable-next-line import/no-cycle
import TimeandExpenseActions from '../../common/buttons/TimeandExpenseActions/TimeandExpenseActions';
import { generalEnums, rateEnum, submissionEnum } from '../../../utils/enums';
import colors from '../../../scss/colors';
import ExpandIcon from '../../common/Icons/ExpandIcon';
import { removeNotification, setNotification } from '../../../actions/appNotifications';
import { deleteTimesheet } from '../../../actions/timesheets';

export const EditContext = createContext(null);

const FakeArrow = ({ direction }) => {
  return (
    <div className={`${classes.fakeArrow} ${direction === 'left' ? classes.left : classes.right}`}>
      <div className={classes.inner} />
    </div>
  );
};

const baseWorkTypes = [
  { label: 'Straight Time', value: 1, key: 1 },
  { label: 'Overtime', value: 2, key: 2 },
  { label: 'PTO', value: 5, key: 5 }
];

export default function Timesheet() {
  const size = useWindowSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const timesheet = useSelector((state) => state.timesheet.data);
  const workTypes = useSelector((state) => state.constFromApi.worktypes);
  const isApprover = useSelector((state) => state.accessToParts.tAndEApprover);
  const approvers = useSelector((state) => state.user.approvers);
  const commentsaving = useSelector((state) => state.timesheet.commentPending);
  const deletePending = useSelector((state) => state.timesheets.pendingDelete);

  const prevPendingDelete = usePrevious(deletePending);
  const previousTimesheet = usePrevious(timesheet);
  const [submissionType, setSubmissionType] = useState(submissionEnum.WEEKLY);
  const [rateType, setRateType] = useState(rateEnum.HOURLY);
  const [startDateOfPeriod, setStartDateOfPeriod] = useState(false);
  const [isEdit, setIsEdit] = useState(size.width < responsiveSizeTimesheet);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSavaing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [lineToDelete, setLineToDelete] = useState('');
  const [openPopUp, setOpenPop] = useState(false);
  const [error, setError] = useState('');
  const [scroll, setScroll] = useState(0);
  const [created, setCreated] = useState(false);
  const [scrollableRef, setScrollableRef] = useState(null);
  const [scrollablePerDiem, setScrollablePerDiem] = useState(null);
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationQueryParams = useLocation();

  useBlocker(() => setOpenPop('unsavedChanges'), hasChanges);

  const saveHandler = async (withPopUp = true) => {
    if (
      submissionType === submissionEnum.MONTHLY &&
      [timesheetsStatuses(t).processed.apiStatus, timesheetsStatuses(t).approved.apiStatus].includes(
        timesheet.Status.Status
      )
    ) {
      return;
    }
    if (withPopUp) setSavaing(true);
    try {
      if (withPopUp) setHasChanges(false);
      await tneAxiosClient.put(`v1/timesheet?Id=${timesheet.Id}`, {
        ...timesheet,
        TimesheetStatus: timesheetsStatuses(t).draft.apiStatus,
        TimesheetDetails: timesheet.TimesheetDetails.map((item) => ({
          ...item,
          WorkTypeId: item.WorkType.Id
        }))
      });
      dispatch(changeStatus(timesheetsStatuses(t).draft.apiStatus));
      if (withPopUp) setOpenPop('saved');
    } catch (e) {
      basicErrorHandler(dispatch, e.response.data);
    }
    if (withPopUp) setSavaing(false);
  };

  const getApprovers = async (clientId) => {
    dispatch(getListOfApprovers(clientId));
  };

  const submitHandler = () => {
    if (!timesheet.TimesheetDetails.every((item) => item.Location)) {
      setError(t('dashboard.timesheet.validations.location'));
      return;
    }

    if (!timesheet.TimesheetDetails.every((item) => item.WorkType.Id)) {
      setError(t('dashboard.timesheet.validations.worktype'));
      return;
    }

    getApprovers(timesheet.ClientCompanyId);

    setOpenPop('submit');
  };

  const finalSubmitTimesheet = async (approver, cb) => {
    try {
      await tneAxiosClient.put(`v1/timesheet?Id=${timesheet.Id}`, {
        ...timesheet,
        TimesheetStatus: timesheetsStatuses(t).submitted.apiStatus,
        ApproverId: approver,
        TimesheetDetails: timesheet.TimesheetDetails.map((item) => ({
          ...item,
          WorkTypeId: item.WorkType.Id
        }))
      });
      setHasChanges(false);
      dispatch(changeStatus(timesheetsStatuses(t).submitted.apiStatus));
      setIsEdit(false);
      cb();
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
  };

  const deleteHandler = async () => {
    setOpenPop('deleteTS');
  };

  const finalDeleteTSHandler = () => {
    dispatch(deleteTimesheet(timesheet.Id));
  };

  const copyHandler = () => {
    setOpenPop('copy');
  };

  const saveCommentHandler = (comment, commentId = '') => {
    if (commentId) {
      dispatch(updateComment(comment, commentId));
    } else {
      dispatch(saveComment(comment, timesheet.Id));
    }
  };

  const finalRejectTimesheet = async () => {
    try {
      setRejecting(true);
      await tneAxiosClient.post(`v1/action-by-approver`, {
        TimesheetId: timesheet.Id,
        Status: timesheetsStatuses(t).rejected.apiStatus
      });
      dispatch(changeStatus(timesheetsStatuses(t).rejected.apiStatus));
      setOpenPop('rejected');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setRejecting(false);
  };

  const rejectHandler = () => {
    if (timesheet?.TimesheetRejectionReasons.every((comment) => comment.ActionTaken !== 'Pending')) {
      setOpenPop('reject');
      return;
    }
    finalRejectTimesheet();
  };

  const checkIsRejectActive = () =>
    !timesheet?.TimesheetRejectionReasons.every((comment) => comment.ActionTaken !== 'Pending');

  const approveHandler = () => {
    setOpenPop('approve');
  };

  const finalApproveTimesheet = async () => {
    try {
      setApproving(true);
      await tneAxiosClient.post(`v1/action-by-approver`, {
        TimesheetId: timesheet.Id,
        Status: timesheetsStatuses(t).approved.apiStatus,
        Reason: ''
      });
      dispatch(changeStatus(timesheetsStatuses(t).approved.apiStatus));
      setOpenPop('approved');
    } catch (e) {
      basicErrorHandler(dispatch, e.message);
    }
    setApproving(false);
  };

  const deleteLine = () => {
    if (timesheet.TimesheetDetails.length === 1) {
      dispatch(addNewTimesheet());
    }
    const deletedLine = timesheet.TimesheetDetails.find((item) => item.Id === lineToDelete);
    dispatch(deleteLineById(lineToDelete));
    dispatch(
      setNotification({
        isAppStyled: true,
        message: `1 ${t('common.general.deleted')}`,
        duration: 3000,
        type: appNotificationsTypes.SUCCESS,
        action: (
          <p
            role="presentation"
            className={classes.undoBtn}
            onClick={() => {
              dispatch(restoreDeletedTimesheetLine(deletedLine));
              dispatch(removeNotification());
            }}
          >
            {t('common.general.undo')}
          </p>
        )
      })
    );
    setHasChanges(true);
    setOpenPop('');
    setLineToDelete('');
  };

  const getSubmissionType = async (clientId) => {
    const response = await tneAxiosClient.get(`v1/placement/${clientId}`);
    if (response?.data?.Data?.length === 0) {
      setSubmissionType(
        timesheet.TimesheetDetails[0].TimesheetHoursByDates?.length > 8 ? submissionEnum.MONTHLY : submissionEnum.WEEKLY
      );
      return;
    }
    setSubmissionType(response.data.Data.find((item) => item.Id === timesheet.PlacementId).Frequency.toLowerCase());
    setRateType(response.data.Data.find((item) => item.Id === timesheet.PlacementId).RateType.toLowerCase());
  };

  const popUpModals = {
    saved: <SavedPopUp />,
    unsavedChanges: (
      <UnsavedChanges
        saveHandler={saveHandler}
        closeHandler={() => setOpenPop(false)}
        rejectPreventing={setHasChanges}
      />
    ),
    delete: (
      <DeletePopUp
        deleteHandler={() => deleteLine(lineToDelete)}
        closeHandler={() => {
          setOpenPop(false);
          setLineToDelete('');
        }}
        submissionType={submissionType}
      />
    ),
    deleteTS: (
      <DeletePopUp
        deleteHandler={finalDeleteTSHandler}
        closeHandler={() => {
          setOpenPop(false);
        }}
        pending={deletePending}
      />
    ),
    submit: <SubmitModal submitHandler={finalSubmitTimesheet} approvers={approvers} Id={timesheet.Id} />,
    approved: (
      <FinalModal queryParams={locationQueryParams.search} message={t('dashboard.timesheet.approvedsuccessfully')} />
    ),
    approve: <ConfirmationModal confirm={finalApproveTimesheet} pending={approving} />,
    rejected: <FinalModal message={t('dashboard.timesheet.rejectedsuccessfully')} />,
    reject: <RejectModal />,
    copy: <CopyModal timesheet={timesheet} withExternalRedirect />
  };

  function alertUser(event) {
    if (!hasChanges) return;
    event.preventDefault();
    // eslint-disable-next-line no-return-assign,consistent-return
    return (event.returnValue = '');
  }

  useEffect(() => {
    if (prevPendingDelete && !deletePending) {
      navigate('/dashboard/timesheets', { state: { idToDelete: timesheet.Id } });
    }
  }, [prevPendingDelete, deletePending]);

  useEffect(() => {
    getSubmissionType(timesheet.ClientCompanyId);
  }, [timesheet]);

  useEffect(() => {
    if (timesheet && !previousTimesheet)
      setStartDateOfPeriod(timesheet.TimesheetDetails[0].TimesheetHoursByDates[0].Day);
  }, [timesheet]);

  useEffect(() => {
    const id = setInterval(() => {
      if (isEdit && hasChanges) {
        saveHandler(false);
      }
    }, 120000);
    return () => clearInterval(id);
  }, [isEdit, hasChanges]);

  const closeHandler = () => {
    navigate(`/dashboard/timesheets${locationQueryParams.search}`);
  };

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [hasChanges]);

  useEffect(() => {
    return () => {
      if (hasChanges) setOpenPop('unsavedChanges');
    };
  }, []);

  useEffect(() => {
    if (lineToDelete) setOpenPop('delete');
  }, [lineToDelete]);

  useEffect(() => {
    const approverModal = searchParams.get('approver');
    const isOnlySubmitted = timesheet.Status.Status === timesheetsStatuses(t).submitted.apiStatus;
    if (approverModal === 'true' && isOnlySubmitted) {
      approveHandler();
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isApprover && size.width < responsiveSizeTimesheet) setIsEdit(size.width < responsiveSizeTimesheet);
  }, [size.width]);

  useEffect(() => {
    const editMode = searchParams.get('editMode');
    const hasChangesMode = searchParams.get('hasChangesMode');
    const flag = searchParams.get('flag');

    if (flag === 'created') {
      setCreated(true);
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.timesheet.timesheet')} ${t('common.general.created').toLowerCase()}`,
          duration: 3000,
          bottom: '147px',
          type: appNotificationsTypes.SUCCESS
        })
      );

      setSearchParams({
        ...getSerializedSearchParams(),
        flag: ''
      });
    }

    if (
      (editMode === 'true' && timesheet?.Status?.Status === timesheetsStatuses(t).draft.apiStatus) ||
      ([timesheetsStatuses(t).draft.apiStatus, timesheetsStatuses(t).rejected.apiStatus].includes(
        timesheet?.Status?.Status
      ) &&
        !isApprover)
    ) {
      setIsEdit(true);
    }
    if (hasChangesMode === 'true') setHasChanges(true);
  }, [searchParams]);

  const srcollLeftRight = (direction) => {
    if (!scrollableRef.current || !scrollablePerDiem.current) return;
    if (direction === 'left') {
      scrollableRef.current.scrollLeft -= 17.1;
      scrollablePerDiem.current.scrollLeft -= 17.1;
    } else {
      scrollableRef.current.scrollLeft += 17.1;
      scrollablePerDiem.current.scrollLeft += 17.1;
    }
  };

  const scrollByArrowsKeys = (e) => {
    if (e.key === 'ArrowRight') {
      if (scroll >= 100) return;
      srcollLeftRight('right');
      setScroll(scroll + 1);
    }

    if (e.key === 'ArrowLeft') {
      if (scroll <= 0) return;
      srcollLeftRight('left');
      setScroll(scroll - 1);
    }
  };

  const scrollByScroll = (e) => {
    if (size.width < responsiveSizeTimesheet) return;
    e.preventDefault();
    if (e.deltaY < 0) {
      if (scroll >= 100) return;
      srcollLeftRight('right');
      setScroll(scroll + 1);
    }

    if (e.deltaY > 0) {
      if (scroll <= 0) return;
      srcollLeftRight('left');
      setScroll(scroll - 1);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', scrollByArrowsKeys);

    if (scroll === 100 && scrollableRef?.current && scrollablePerDiem?.current) {
      scrollableRef.current.scrollLeft += scrollableRef.current.children[0].scrollWidth;
      scrollablePerDiem.current.scrollLeft += scrollableRef.current.children[0].scrollWidth;
    }
    if (scroll === 0 && scrollableRef?.current && scrollablePerDiem?.current) {
      scrollableRef.current.scrollLeft = 0;
      scrollablePerDiem.current.scrollLeft = 0;
    }

    return () => {
      window.removeEventListener('keydown', scrollByArrowsKeys);
    };
  }, [scrollableRef, scrollablePerDiem, scroll]);

  const contextValue = useMemo(
    () => ({ isEdit, setIsEdit, timesheet, hasChanges, setHasChanges }),
    [isEdit, setIsEdit, timesheet, hasChanges, setHasChanges]
  );

  const getMonthPeriod = useMemo(
    () =>
      months(t)[
        new Date(
          timesheet?.TimesheetDetails[0].TimesheetHoursByDates.find((item) => new Date(item?.Day).getDate() === 1)?.Day
        ).getMonth()
      ],
    [timesheet?.TimesheetDetails]
  );

  const getWeekPeriod = useMemo(
    () =>
      `${
        dayjs(timesheet.TimesheetDetails[0]?.TimesheetHoursByDates[0].Day).isSame(
          timesheet.TimesheetDetails[0]?.TimesheetHoursByDates.at(-1).Day,
          'month'
        )
          ? dayjs(timesheet.TimesheetDetails[0]?.TimesheetHoursByDates[0].Day).format('D')
          : dayjs(timesheet.TimesheetDetails[0]?.TimesheetHoursByDates[0].Day).format('D MMM')
      } - ${dayjs(timesheet.TimesheetDetails[0]?.TimesheetHoursByDates.at(-1).Day).format('D MMM')}`,
    [timesheet?.TimesheetDetails]
  );

  return (
    <EditContext.Provider value={contextValue}>
      <section className={classes.mainContainer}>
        {!(size.width > responsiveSizeTimesheet) ? (
          <NavigationComponent
            label={submissionType === submissionEnum.MONTHLY ? getMonthPeriod : getWeekPeriod}
            linkBack={!created ? `/dashboard/timesheets${locationQueryParams.search}` : <div />}
          >
            {!created ? (
              <MobileHeadMenu
                isApprover={isApprover}
                data={timesheet}
                deleteHandler={deleteHandler}
                copyHandler={copyHandler}
                pageType={generalEnums.TIMESHEET}
              />
            ) : (
              <CloseIcon onClick={() => navigate('/dashboard/timesheets')} />
            )}
          </NavigationComponent>
        ) : (
          <div className={classes.closeContainer}>
            <button onClick={closeHandler}>
              <CloseIcon />
            </button>
          </div>
        )}
        <TimesheetsHeader
          timesheet={timesheet}
          submissionType={submissionType}
          isApprover={isApprover}
          created={created}
        />
        {workTypes && (
          <MainTable
            scrollByScroll={scrollByScroll}
            data={timesheet?.TimesheetDetails || []}
            perdiems={timesheet?.TimesheetPerDiem || []}
            submissionType={submissionType}
            rateType={rateType}
            startDateOfPeriod={startDateOfPeriod}
            setStartDateOfPeriod={setStartDateOfPeriod}
            saving={saving}
            rejecting={rejecting}
            save={saveHandler}
            submit={submitHandler}
            approve={approveHandler}
            reject={rejectHandler}
            isRejectActive={checkIsRejectActive()}
            deleteLine={setLineToDelete}
            timesheetInfo={{
              status: timesheet.Status.Status,
              client: timesheet.ClientName,
              placement: timesheet.Placement,
              po: timesheet.PO,
              recordNo: timesheet.RecordNo,
              user: timesheet.User,
              isApprover
            }}
            setScrollableRef={setScrollableRef}
            setScrollablePerDiem={setScrollablePerDiem}
            scroll={scroll}
            created={created}
          />
        )}
        {/* TODO need refactor: move to separate component */}
        {submissionType === submissionEnum.MONTHLY && size.width > responsiveSizeTimesheet && (
          <div className={classes.sliderContainer}>
            {scroll === 0 ? (
              <FakeArrow direction="left" />
            ) : (
              <button
                onClick={() => {
                  srcollLeftRight('left');
                  setScroll(scroll - 1);
                }}
              >
                <ExpandIcon rotate="90deg" />
              </button>
            )}
            <div className={classes.innerContainer}>
              <div className={classes.composableContainer} />
              <Slider
                value={scroll}
                onChange={(e) => {
                  if (e.target.value === scroll) return;
                  if (e.target.value > scroll) {
                    scrollableRef.current.scrollLeft += 11 * (e.target.value - scroll);
                    scrollablePerDiem.current.scrollLeft += 11 * (e.target.value - scroll);
                  } else {
                    scrollableRef.current.scrollLeft -= 11 * (scroll - e.target.value);
                    scrollablePerDiem.current.scrollLeft -= 11 * (scroll - e.target.value);
                  }
                  setScroll(e.target.value);
                }}
                size="small"
                defaultValue={0}
                aria-label="Small"
                sx={{
                  width: 'calc(100% - 75% / 2)',
                  height: '10px',
                  backgroundColor: 'transparent',
                  '& .MuiSlider-thumb:hover': { boxShadow: 'none' },
                  '& .Mui-active': { boxShadow: 'none' },
                  '& .MuiSlider-thumb': {
                    width: '60%',
                    backgroundColor: colors.NAVY_BLUE,
                    height: '10px',
                    borderRadius: '5px 5px 5px 5px',
                    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                      boxShadow: 'inherit'
                    }
                  },
                  '& .MuiSlider-track': { backgroundColor: 'transparent' },
                  '& .MuiSlider-rail': { backgroundColor: '#E1E6EA', borderRadius: '5px 5px 5px 5px' }
                }}
              />
            </div>
            {scroll === 100 ? (
              <FakeArrow direction="right" />
            ) : (
              <button
                onClick={() => {
                  srcollLeftRight('right');
                  setScroll(scroll + 1);
                }}
              >
                <ExpandIcon rotate="-90deg" />
              </button>
            )}
          </div>
        )}
        <div className={classes.bottomSection}>
          {workTypes && (
            <TotalSection
              data={timesheet?.TimesheetDetails || []}
              workTypes={[...workTypes, ...baseWorkTypes]}
              rateType={rateType}
            />
          )}
          <Attachments
            status={timesheet.Status.Status}
            editeMode={isEdit}
            timesheetId={params?.id}
            attachments={timesheet?.TimesheetAttachments}
            handleChanges={setHasChanges}
          />
        </div>
        {([
          timesheetsStatuses(t).rejected.apiStatus,
          timesheetsStatuses(t).draft.apiStatus,
          timesheetsStatuses(t).approved.apiStatus,
          timesheetsStatuses(t).processed.apiStatus,
          timesheetsStatuses(t).submitted.apiStatus
        ].includes(timesheet.Status.Status) ||
          (isApprover && timesheet.Status.Status === timesheetsStatuses(t).submitted.apiStatus)) && (
          <ApproverComment
            comments={timesheet?.TimesheetRejectionReasons.sort((a, b) => (a.Id > b.Id ? -1 : 1))}
            disabled={
              !isApprover ||
              [
                timesheetsStatuses(t).approved.apiStatus,
                timesheetsStatuses(t).processed.apiStatus,
                timesheetsStatuses(t).rejected.apiStatus
              ].includes(timesheet.Status.Status)
            }
            isApprover={isApprover}
            extraClass={classes.commentSection}
            saveComment={saveCommentHandler}
            commentSaving={commentsaving}
          />
        )}
        {!(size.width > responsiveSizeTimesheet) && (
          <div className={classes.actions}>
            <TimeandExpenseActions
              saving={saving}
              rejecting={rejecting}
              save={saveHandler}
              submit={submitHandler}
              approve={approveHandler}
              reject={rejectHandler}
              isRejectActive={checkIsRejectActive()}
              submissionType={submissionType}
              isApprover={isApprover}
              status={timesheet.Status.Status}
            />
          </div>
        )}
      </section>
      <BaseModal
        open={openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop('')}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={1}
        backgroundColor={openPopUp === 'copy' ? colors.PAGE_BACKGROUND : undefined}
      >
        <WithCloseIcon close={() => setOpenPop('')} y={6}>
          {popUpModals[openPopUp]}
        </WithCloseIcon>
      </BaseModal>
      {error && (
        <NotificationModal open={error} onClose={() => setError('')}>
          <p className={classes.errorMessage}>{error}</p>
        </NotificationModal>
      )}
    </EditContext.Provider>
  );
}
